import type { TFunction } from "i18next";

/**
 * The coach's name, or the words that stand in for it.
 *
 * The backend resolves the name for every public page (the workspace's brand, else the payment
 * account's business name, else the coach's own name), so the fallback words are for a seller
 * with no name at all.
 * Every sentence on these pages names the coach, and "I agree to 's terms" is what a gap looks
 * like. So the name is resolved once, here, and every interpolation takes the result.
 *
 * The header is a different question: a header is a signature, and a signature that reads
 * "Coaching by your coach" is worse than no header, so the pages draw none when the name is
 * missing. This is only for the sentences that cannot be left with a hole in them.
 */
export function coachLabel(name: string | undefined, t: TFunction): string {
  const trimmed = name?.trim();
  return trimmed ? displayName(trimmed) : t("gymkeePay.common.yourCoach");
}

/**
 * The name as a person would write it.
 *
 * Rendered as the coach typed it, and never upper-cased by us. The other half of that rule is
 * the name that ARRIVES shouting: "Mickael LEOCADIE" is what a coach typed into a settings field
 * once, and it now runs across the top of every page his clients pay on and, inside a sentence
 * ("MICKAEL LEOCADIE's terms"), stops reading as a name at all. So any word of five letters or
 * more that is entirely capitals is title-cased for display.
 *
 * Five and not two, on purpose: a company form is a real word in a business name and it is
 * written in capitals by law. SAS, EI, SARL, SASU and EURL all survive; a surname does not.
 * The raw value is what the rest of the app keeps using.
 */
export function displayName(name: string): string {
  return name.replace(
    /\p{Lu}{5,}/gu,
    (word) => word.charAt(0) + word.slice(1).toLocaleLowerCase(),
  );
}

/**
 * A sentence that opens with an interpolated name.
 *
 * "{{coach}} has your payment" reads correctly when the coach is Mickael Leocadie and reads like
 * a typo when the name is missing and the fallback words take its place: "your coach has your
 * payment". Every language this app ships capitalises the first letter of a sentence, so the
 * sentence is capitalised rather than the fallback: capitalising the fallback itself would put a
 * capital in the middle of "I agree to Your coach's terms".
 */
export function sentence(text: string): string {
  if (!text) return text;
  const first = text.charAt(0);
  return first === first.toLocaleUpperCase()
    ? text
    : first.toLocaleUpperCase() + text.slice(1);
}

/** The part of a public payload that names the seller. */
export interface SellerSource {
  name?: string;
  kind?: "workspace" | "coach";
  /** The business mark: the workspace logo, else the payment account's icon. */
  icon?: string;
  /** The coach's own photo; the backend never sends one for a team. */
  photo?: string;
}

/** Who signs the page: the seller's name and the mark beside it, in the seller's shape. */
export interface Seller {
  name: string;
  /** The picture in the mark, when there is one; the initials of the name otherwise. */
  src?: string;
  /** A business is the square workspace tile, a person the round avatar (avatar 2.2). */
  shape: "workspace" | "person";
}

/** A picture from the API is drawn only over https; anything else falls back to the initials. */
function httpsUrl(value: string | undefined): string | undefined {
  const url = value?.trim();
  return url && /^https:\/\//i.test(url) ? url : undefined;
}

/**
 * The seller for the coach header, or null when the seller truly has no name.
 *
 * The backend resolves the seller per workspace (gymkee/backend#299): a team signs with its name
 * and its mark, a coach on his own with his brand, his mark and his photo. A business mark is
 * drawn square and a person's photo round, so a business never reads as a person (checkout-form
 * 1.3). The wide logo is never used: cover-cropped into a mark it leaves a fragment of a
 * wordmark. With neither picture the initials stand in, square for a team and round for a coach.
 * With no name there is no header: "Coaching by your coach" under a blank glyph is a signature
 * from nobody, and the Gymkee Pay bar above the sheet already says whose page this is
 * (checkout 1.6).
 */
export function sellerOf(business: SellerSource | undefined): Seller | null {
  const name = business?.name?.trim();
  if (!name) return null;
  const mark = httpsUrl(business?.icon);
  if (mark) return { name, src: mark, shape: "workspace" };
  const photo = business?.kind === "workspace" ? undefined : httpsUrl(business?.photo);
  if (photo) return { name, src: photo, shape: "person" };
  return { name, shape: business?.kind === "workspace" ? "workspace" : "person" };
}
